import { motion, useReducedMotion } from "framer-motion";
import { LineChart, Scale, Target } from "lucide-react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { Seo } from "../components/layout/Seo";
import { Button } from "../components/ui/Button";
import { TextLink } from "../components/ui/TextLink";
import { isLocale, localizedPath } from "../lib/routes";

const S = "py-[clamp(36px,5vw,48px)]";
const scrollMt = "scroll-mt-28";

const principles = [
  [LineChart, "Evidence-led", "Real needs. Real data. Real impact."],
  [Scale, "Technology-neutral", "The right solution for each context."],
  [Target, "Pilot-driven", "From proof of concept to scalable models."]
];

const programIndex = [
  ["Energy Resilience", "energy-resilience"],
  ["Climate-Smart Agriculture", "climate-smart-agriculture"],
  ["Green Wine Moldova", "green-wine-moldova"],
  ["Energy Communities", "energy-communities"]
];

const wineColumns = [
  ["VINEYARD", "Agrivoltaics, water and crop resilience"],
  ["WINERY", "Storage, energy management and efficiency"],
  ["FINANCE", "Grants, green finance and investment pathways"],
  ["EVIDENCE", "Measurable impact, replication and knowledge transfer"]
];

const pathwaySteps = [
  ["01", "Research", "Understand the problem, market and operating context."],
  ["02", "Feasibility", "Test technical fit, economics, regulation and financing."],
  ["03", "Partner design", "Match the right Moldovan site, Israeli expertise and institutional support."],
  ["04", "Pilot", "Deploy on a limited, measurable scale."],
  ["05", "Evidence", "Measure performance, economics and practical constraints."],
  ["06", "Replication", "Turn what worked into a model others can use."]
];

const ecosystem = [
  ["Moldova", "Farms, wineries, food processors, municipalities, universities and local institutions that can define real problems and host real-world pilots.", "border-brand-green", "bg-brand-green"],
  ["Israel", "Technology companies, engineers, researchers and sector experts with solutions and operating experience relevant to Moldova.", "border-brand-blue", "bg-brand-blue"],
  ["International", "Development institutions, financing facilities, donors and research programmes that can support feasibility, pilots and replication.", "border-brand-cyan", "bg-brand-cyan"]
];

function Reveal({ children, delay = 0, className = "" }: { children: ReactNode; delay?: number; className?: string }) {
  const reduced = useReducedMotion();
  if (reduced) return <div className={className}>{children}</div>;
  return (
    <motion.div className={className} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay }}>
      {children}
    </motion.div>
  );
}

function Chips({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-wrap gap-2">
      {items.map((item) => (
        <li key={item} className="rounded-full border border-line bg-surface px-3.5 py-1.5 text-sm font-semibold text-ink-secondary">{item}</li>
      ))}
    </ul>
  );
}

function Label({ children }: { children: ReactNode }) {
  return <p className="text-xs font-bold uppercase tracking-[0.16em] text-ink-secondary">{children}</p>;
}

export function Programs() {
  const { locale } = useParams();
  const activeLocale = isLocale(locale) ? locale : "en";

  return (
    <>
      <Seo title="Programs" description="TransitionBridge programs connect Israeli expertise with Moldova’s transition priorities through research, feasibility, pilot projects and evidence-based scaling." />

      {/* SECTION 1 — INTRO */}
      <section className="bg-bg-primary pt-[calc(var(--header-height)+40px)] pb-14">
        <div className="container-grid">
          <div className="max-w-[860px]">
            <p className="eyebrow mb-5">PROGRAMS</p>
            <h1 className="text-balance text-[clamp(2.25rem,5.5vw,4.2rem)] font-extrabold leading-[1.03]">From shared expertise to working programs</h1>
            <p className="mt-7 max-w-[720px] text-lg leading-8 text-ink-secondary md:text-xl">TransitionBridge works through focused program areas where Israeli technology and experience can address Moldova’s transition priorities.</p>
            <p className="mt-4 max-w-[720px] text-lg leading-8 text-ink-secondary md:text-xl">Each program follows the same discipline: evidence first, feasibility second, pilot before scale.</p>
          </div>
          <div className="mt-12 grid gap-8 border-t border-line pt-8 sm:grid-cols-3">
            {principles.map(([Icon, title, body]) => {
              const I = Icon as typeof LineChart;
              return (
                <div key={title as string} className="flex items-start gap-4">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-brand-blue/15 bg-brand-blue/[0.06] text-brand-blue"><I className="h-5 w-5" aria-hidden="true" /></span>
                  <div>
                    <h3 className="font-bold">{title as string}</h3>
                    <p className="mt-1 text-sm leading-6 text-ink-secondary">{body as string}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ARCHITECTURE VISUAL + PROGRAM INDEX */}
      <section className="bg-bg-primary pb-[clamp(48px,6vw,72px)]">
        <div className="container-grid">
          <div className="mb-7 max-w-[720px]">
            <p className="eyebrow mb-3">PROGRAM ARCHITECTURE</p>
            <h2 className="text-balance text-2xl font-bold md:text-3xl">How our programs connect</h2>
          </div>
          <img
            src="/assets/programs/programs-architecture-hero.webp"
            alt="TransitionBridge program architecture connecting Israeli expertise with Moldovan priorities through research, feasibility, pilots and scale."
            width={1672}
            height={941}
            className="hidden w-full rounded-3xl border border-line object-contain md:block"
          />
          {/* Mobile: semantic HTML compact version */}
          <div className="md:hidden">
            <div className="relative grid gap-6 border-s border-line ps-6">
              {[
                ["Israel expertise", ""],
                ["TransitionBridge", ""],
                ["Energy Resilience · Climate-Smart Agriculture · Energy Communities", ""],
                ["Research · Feasibility · Partner Design · Pilot · Evidence · Scale", ""],
                ["Green Wine Moldova", "flagship"]
              ].map(([text, kind], i) => (
                <div key={i} className="relative">
                  <span className={`absolute -start-[31px] top-1.5 h-3 w-3 rounded-full ${kind === "flagship" ? "bg-brand-burgundy" : "bg-brand-cyan"}`} aria-hidden="true" />
                  <p className={`text-base font-semibold ${kind === "flagship" ? "text-brand-burgundy" : "text-ink-primary"}`}>{text}</p>
                </div>
              ))}
            </div>
          </div>
          <nav aria-label="Programs on this page" className="mt-8">
            <ul className="flex flex-wrap gap-2">
              {programIndex.map(([label, id]) => (
                <li key={id}>
                  <a href={`#${id}`} className="inline-flex rounded-full border border-line bg-surface px-4 py-2 text-sm font-semibold text-ink-secondary transition-colors hover:border-brand-blue/40 hover:text-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-[3px] focus-visible:ring-offset-bg-primary">{label}</a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </section>

      {/* SECTION 2 — PROGRAM AREAS INTRO */}
      <section className="bg-bg-primary pt-0 pb-[clamp(20px,3vw,24px)]">
        <div className="container-grid">
          <div className="max-w-[760px]">
            <p className="eyebrow mb-4">PROGRAM AREAS</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">Three areas. One transition logic.</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">Our program areas define where we build long-term expertise. Individual initiatives may combine two or more of them.</p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — ENERGY RESILIENCE */}
      <section id="energy-resilience" className={`${scrollMt} bg-bg-primary ${S}`}>
        <div className="container-grid grid items-center gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <div>
            <p className="eyebrow mb-4">PROGRAM AREA 01</p>
            <h2 className="text-3xl font-bold leading-tight md:text-[2.4rem]">Energy Resilience</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">Helping energy-intensive farms and agri-food businesses become less exposed to price volatility, grid interruptions and inefficient energy use.</p>
            <div className="mt-7">
              <Label>Focus</Label>
              <div className="mt-3"><Chips items={["Energy storage", "Energy management", "Efficiency", "Flexible demand", "Resilient infrastructure"]} /></div>
            </div>
            <p className="mt-7 leading-8 text-ink-secondary">We examine consumption profiles, operating constraints and financing options before selecting a technology pathway. The objective is a measurable business case for resilience, not a technology demonstration.</p>
            <div className="mt-7 rounded-2xl border border-line bg-surface px-5 py-4">
              <Label>Typical pathway</Label>
              <p className="mt-2 text-sm font-semibold text-ink-primary">Load data → feasibility → technology fit → financing → pilot → monitoring</p>
            </div>
          </div>
          <div className="overflow-hidden rounded-3xl border border-line">
            <img src="/assets/programs/programs-energy-resilience_1.webp" alt="Energy-resilient winery combining solar generation, battery storage, grid connection and energy management." width={1586} height={992} loading="lazy" decoding="async" className="aspect-[8/5] w-full object-cover" />
          </div>
        </div>
      </section>

      {/* SECTION 4 — CLIMATE-SMART AGRICULTURE */}
      <section id="climate-smart-agriculture" className={`${scrollMt} bg-bg-secondary ${S}`}>
        <div className="container-grid grid items-center gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16">
          <div className="order-2 overflow-hidden rounded-3xl border border-line lg:order-1">
            <img src="/assets/programs/programs-climate-smart-agriculture.webp" alt="Vineyard using agrivoltaics, irrigation and agricultural monitoring technologies." width={1586} height={992} loading="lazy" decoding="async" className="aspect-[8/5] w-full object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="eyebrow mb-4">PROGRAM AREA 02</p>
            <h2 className="text-3xl font-bold leading-tight md:text-[2.4rem]">Climate-Smart Agriculture</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">Building resilience where energy, water and agriculture meet.</p>
            <p className="mt-4 leading-8 text-ink-secondary">Moldova’s agricultural transition is not only an energy challenge. Heat, water stress, irrigation efficiency and crop protection increasingly interact with the way farms produce and use energy.</p>
            <p className="mt-4 leading-8 text-ink-secondary">This program explores solutions that can improve agricultural resilience while preserving the productive use of land.</p>
            <div className="mt-7">
              <Label>Focus</Label>
              <div className="mt-3"><Chips items={["Agrivoltaics", "Water management", "Smart irrigation", "Crop protection", "Protected agriculture"]} /></div>
            </div>
            <div className="mt-6 border-t border-line pt-4">
              <Label>Primary contexts</Label>
              <p className="mt-2 leading-7 text-ink-secondary">Vineyards, orchards, greenhouses and water-intensive agriculture.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 — GREEN WINE MOLDOVA (FLAGSHIP) */}
      <section id="green-wine-moldova" className={`${scrollMt} bg-[#F6EEEE] ${S}`}>
        <div className="container-grid">
          <div className="max-w-[860px]">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.16em] text-brand-burgundy">FLAGSHIP INITIATIVE · PILOT PREPARATION</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] text-brand-burgundy md:text-[2.8rem]">Green Wine Moldova</h2>
            <p className="mt-5 text-2xl font-semibold leading-snug text-ink-primary md:text-3xl">From sustainable vineyard to sustainable bottle.</p>
            <p className="mt-5 max-w-[760px] leading-8 text-ink-secondary">Green Wine Moldova is TransitionBridge’s first integrated sector initiative. It brings together vineyard resilience, winery energy use, water efficiency, Israeli technologies, financing pathways and measurable sustainability outcomes within one pilot framework.</p>
          </div>
          <div className="mt-12 overflow-hidden rounded-3xl border border-brand-burgundy/15">
            <img src="/assets/programs/programs-green-wine-moldova.webp" alt="Sustainable vineyard and winery combining agrivoltaics, energy storage and modern energy infrastructure." width={1916} height={821} loading="lazy" decoding="async" className="aspect-[7/3] w-full object-cover" />
          </div>
          <div className="mt-9 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {wineColumns.map(([title, body]) => (
              <div key={title} className="border-t-2 border-brand-burgundy/30 pt-5">
                <h3 className="text-sm font-extrabold uppercase tracking-[0.14em] text-brand-burgundy">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-ink-secondary">{body}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-[820px] border-l-4 border-brand-burgundy ps-6 text-lg font-semibold text-ink-primary">One sector becomes a testing ground for several transition technologies working together.</p>
        </div>
      </section>

      {/* SECTION 6 — COMMON PROGRAM PATHWAY */}
      <section id="program-pathway" className={`${scrollMt} bg-bg-primary ${S}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <p className="eyebrow mb-4">HOW WE WORK</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">From question to working pilot</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">We do not begin with a preferred product. We begin with a problem, a site and evidence.</p>
          </div>

          {/* Desktop horizontal pathway */}
          <div className="relative mt-12 hidden lg:block">
            <div className="absolute left-0 right-0 top-4 h-px bg-[linear-gradient(90deg,rgba(36,87,245,0.45),rgba(18,175,203,0.45))]" aria-hidden="true" />
            <ol className="grid grid-cols-6 gap-6">
              {pathwaySteps.map(([num, title, body], i) => {
                const pivot = num === "04";
                return (
                  <Reveal key={num} delay={i * 0.06}>
                    <li className="relative">
                      <span className={`absolute left-0 top-0 grid h-8 w-8 place-items-center rounded-full text-xs font-bold ${pivot ? "bg-brand-blue text-white shadow-[0_0_0_4px_rgba(36,87,245,0.14)]" : "border border-brand-cyan/50 bg-bg-primary text-brand-cyanDark"}`} aria-hidden="true">{num}</span>
                      <div className="pt-12">
                        <h3 className={`text-[1.0625rem] font-semibold ${pivot ? "text-brand-blue" : "text-ink-primary"}`}>{title}</h3>
                        <p className="mt-2 text-sm leading-6 text-ink-secondary">{body}</p>
                      </div>
                    </li>
                  </Reveal>
                );
              })}
            </ol>
          </div>

          {/* Mobile vertical pathway */}
          <div className="relative mt-10 lg:hidden">
            <div className="absolute left-[15px] top-3 bottom-3 w-px bg-[linear-gradient(180deg,rgba(36,87,245,0.45),rgba(18,175,203,0.45))]" aria-hidden="true" />
            <ol className="grid gap-7">
              {pathwaySteps.map(([num, title, body]) => {
                const pivot = num === "04";
                return (
                  <li key={num} className="relative ps-12">
                    <span className={`absolute start-0 top-0 grid h-8 w-8 place-items-center rounded-full text-xs font-bold ${pivot ? "bg-brand-blue text-white" : "border border-brand-cyan/50 bg-bg-primary text-brand-cyanDark"}`} aria-hidden="true">{num}</span>
                    <h3 className={`text-[1.0625rem] font-semibold ${pivot ? "text-brand-blue" : "text-ink-primary"}`}>{title}</h3>
                    <p className="mt-1.5 text-sm leading-6 text-ink-secondary">{body}</p>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* SECTION 7 — ENERGY COMMUNITIES (RESEARCH TRACK) */}
      <section id="energy-communities" className={`${scrollMt} bg-[#EEF3F2] ${S}`}>
        <div className="container-grid grid items-center gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <p className="eyebrow">PROGRAM AREA 03</p>
              <span className="rounded-full border border-brand-cyan/40 bg-brand-cyan/10 px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.14em] text-brand-cyanDark">Exploratory research</span>
            </div>
            <h2 className="text-3xl font-bold leading-tight md:text-[2.4rem]">Energy Communities</h2>
            <p className="mt-5 leading-8 text-ink-secondary">We are exploring community-scale energy models for municipalities, public institutions and local communities where resilience, public benefit and education matter alongside project economics.</p>
            <div className="mt-7">
              <Label>Focus</Label>
              <div className="mt-3"><Chips items={["Community energy", "Public-benefit pilots", "Local resilience", "Education", "Replicable models"]} /></div>
            </div>
          </div>
          <div className="overflow-hidden rounded-3xl border border-line">
            <img src="/assets/programs/programs-energy-communities_1.webp" alt="Community energy model with a public building, rooftop solar, battery storage and local infrastructure." width={1672} height={941} loading="lazy" decoding="async" className="aspect-[16/9] w-full object-cover" />
          </div>
        </div>
      </section>

      {/* SECTION 8 — ECOSYSTEM */}
      <section id="ecosystem" className={`${scrollMt} bg-bg-primary pt-[clamp(36px,5vw,48px)] pb-[clamp(40px,5vw,52px)]`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <p className="eyebrow mb-4">ECOSYSTEM</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">Built between ecosystems</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">Programs are shaped through cooperation across three complementary environments.</p>
          </div>
          <div className="mt-8 grid gap-10 md:grid-cols-3 md:gap-8">
            {ecosystem.map(([title, body, border, dot]) => (
              <div key={title} className={`border-t-2 ${border} pt-6`}>
                <div className="mb-3 flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${dot}`} aria-hidden="true" />
                  <h3 className="text-xl font-bold">{title}</h3>
                </div>
                <p className="leading-7 text-ink-secondary">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-bg-primary">
        <div className="container-grid">
          <div className="h-px w-full bg-[linear-gradient(90deg,#2457f5,#12afcb,#28c98b)]" aria-hidden="true" />
        </div>
        <div className="container-grid pt-[clamp(40px,5vw,52px)] pb-[clamp(48px,6vw,64px)]">
          <div className="max-w-[820px]">
            <p className="eyebrow mb-4">WORK WITH US</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">Bring us a problem, a site or a technology.</h2>
            <p className="mt-5 max-w-[720px] text-lg leading-8 text-ink-secondary">We are open to conversations with Moldovan operators, Israeli technology teams and institutions supporting sustainable transition.</p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
              <Button to={localizedPath(activeLocale, "contact")}>Start a conversation</Button>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <TextLink to={localizedPath(activeLocale, "moldova")} tone="dark">Explore Moldova</TextLink>
                <TextLink to={localizedPath(activeLocale, "israel")} tone="dark">Explore Israel</TextLink>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
